import React, { useCallback, useEffect, useRef, useState } from "react";
import { uploadChunked } from "../lib/upload";
import { toast } from "./ui/sonner";
import { Button } from "./ui/button";
import {
  AlertTriangle, Bot, CheckCircle2, ChevronDown, ChevronRight, CircleSlash, Clock, Cpu,
  Loader2, Lock, Paperclip, Plus, RotateCcw, ShieldAlert, ShieldCheck, Unlock, UserRound,
} from "lucide-react";
import { fmtMD } from "../lib/date";

import { T } from "../lib/i18n";
import { useApp } from "../context/AppContext";
import { AssigneeOptions, CoTag } from "./engine/AssigneeOptions";

// 引擎面板管理名单：与后端 engine_module.MANAGE_ROLES 同源同步（2026-10-24 加入 cdc）。
// 决定是否展示「拆除判定(需要/无需) / Permit·Set1 Gate / 指派」等管理控件。
// 背景：线上项目组「线上 PM」账号角色实为 cdc，此前面板对 cdc 隐藏拆除判定按钮，导致拆除阶段无人能解锁；
// 811 / VR 面板已放开 cdc，此处对齐。人工 Release 仍走更严的 RELEASE_ROLES，不随本名单放开。
const MANAGE = ["admin", "ceo", "eng_manager", "pm", "cdc"];
// 【2026-10-26 扩面第九批】两个名单与 /board::ops-manage · ::task-release 的目录默认一致 → 直接读同一点
const RELEASE_ROLES = ["admin", "ceo", "eng_manager"];
const ROLE_COLOR = {
  crew: "bg-orange-100 text-orange-700", owner: "bg-blue-100 text-blue-700", cdc: "bg-violet-100 text-violet-700",
  sub: "bg-teal-100 text-teal-700", vendor: "bg-pink-100 text-pink-700", pm: "bg-slate-200 text-slate-700",
  city: "bg-red-100 text-red-700", sales: "bg-emerald-100 text-emerald-700", system: "bg-slate-100 text-slate-500",
};
const STATE_META = {
  ready: ["可执行", "Ready", "bg-emerald-100 text-emerald-700"],
  pending: ["待办", "Pending", "bg-slate-100 text-slate-500"],
  done: ["已完成", "Done", "bg-emerald-50 text-emerald-600"],
  skipped: ["已跳过", "Skipped", "bg-slate-100 text-slate-400"],
  waiting_time: ["等待时间", "Waiting (time)", "bg-amber-100 text-amber-700"],
  waiting_external: ["等待外部", "Waiting (external)", "bg-blue-100 text-blue-700"],
  blocked: ["被阻塞", "Blocked", "bg-red-100 text-red-700"],
  locked: ["未解锁", "Locked", "bg-slate-50 text-slate-400"],
};
const NEXT_META = {
  READY: ["bg-emerald-600", "READY · 可执行", "READY"],
  BLOCKED_GATE: ["bg-red-600", "BLOCKED · 被阻塞", "BLOCKED"],
  WAITING_TIME: ["bg-amber-500", "WAITING · 等待时间", "WAITING · time"],
  WAITING_EXTERNAL: ["bg-blue-600", "WAITING · 等待外部", "WAITING · external"],
  completed: ["bg-slate-800", "COMPLETED · 已交付", "COMPLETED"],
};

export async function chunkUpload(api, file, onProgress) {
  const data = await uploadChunked(api, file, { onPct: onProgress });
  return { fileId: data.fileId, contentType: data.contentType || file.type, filename: data.filename || file.name, kind: data.kind, size: data.size };
}

export const EnginePanel = ({ api, id, zh, user, onChanged, refreshTick }) => {
  const { permOf } = useApp();
  const [eng, setEng] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState(null);
  const [openSub, setOpenSub] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [adviceBusy, setAdviceBusy] = useState(false);
  const [evBusy, setEvBusy] = useState(null);
  const [showBlockers, setShowBlockers] = useState(false);
  const [blkForm, setBlkForm] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const evTask = useRef(null);
  const evInput = useRef(null);
  const demoInput = useRef(null);
  const canManage = permOf("/board::ops-manage") ?? MANAGE.includes(user?.role);
  const canRelease = permOf("/board::task-release") ?? RELEASE_ROLES.includes(user?.role);

  const load = useCallback(async (keep) => {
    try {
      const { data: d } = await api.get(`/projects/${id}/engine`);
      setEng(d);
      setTab((t) => (keep && t != null ? t : Math.min(d.phaseIdx, 7)));
      if (!keep) {
        const cur = d.substages.find((s) => s.current);
        setOpenSub(cur ? cur.key : null);
      }
    } catch (e) { setErr(e?.detailText || (zh ? "加载失败" : "Load failed")); }
  }, [api, id, zh]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshTick > 0) load(true); }, [refreshTick, load]);

  const run = async (fn, okMsg) => {
    try {
      const { data: r } = await fn();
      if (okMsg) toast.success(okMsg);
      if (r?.advanced) toast.success(zh ? `🚀 阶段门槛全部满足，已自动推进至「${r.advanced.toName}」` : `Advanced to ${r.advanced.toName}`, { duration: 6000 });
      await load(true);
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
  };

  const complete = (t) => run(() => api.post(`/projects/${id}/engine/tasks/${t.id}/complete`, {}), zh ? "任务已完成 ✓" : "Done");
  const skip = (t) => {
    const reason = window.prompt(zh ? "跳过原因（本项目不适用）：" : "Skip reason:", T("本项目不适用"));
    if (reason === null) return;
    run(() => api.post(`/projects/${id}/engine/tasks/${t.id}/skip`, { reason }), zh ? "已跳过" : "Skipped");
  };
  const waiting = (t) => {
    const note = window.prompt(zh ? "等待外部说明（如：等待市府回复）" : "Waiting note:", T("等待外部回复"));
    if (note === null) return;
    run(() => api.post(`/projects/${id}/engine/tasks/${t.id}/waiting`, { note }), zh ? "已标记等待外部" : "Marked waiting");
  };
  const clearWaiting = (t) => run(() => api.post(`/projects/${id}/engine/tasks/${t.id}/waiting`, { clear: true }), zh ? "已恢复待办" : "Cleared");
  const reopen = (t) => run(() => api.post(`/projects/${id}/engine/tasks/${t.id}/reopen`), zh ? "已重开" : "Reopened");
  const nudge = (t) => run(() => api.post(`/projects/${id}/engine/tasks/${t.id}/nudge`), zh ? "已催办" : "Nudged");
  const assignTask = (t, username) => {
    setAssignFor(null);
    run(() => api.post(`/projects/${id}/engine/tasks/${t.id}/assign`, { username }),
      username ? (zh ? "已指派并通知本人" : "Assigned") : (zh ? "已取消指派" : "Unassigned"));
  };
  const release = (t) => run(() => api.post(`/projects/${id}/engine/release`, { code: t.node, note: T("人工 Release") }), zh ? "已人工 Release，时间闸放行" : "Released");
  const setGate = (k, v) => run(() => api.post(`/projects/${id}/engine/gates`, { [k]: v }));
  const demoYes = () => run(() => api.post(`/projects/${id}/engine/demo-decision`, { needed: true }), zh ? "已确认：需要拆除" : "Demo needed");
  const demoNoFile = async (file) => {
    try {
      const up = await chunkUpload(api, file);
      await run(() => api.post(`/projects/${id}/engine/demo-decision`, { needed: false, waiverFileId: up.fileId }), zh ? "已确认无需拆除，豁免文件已留档" : "No demo, waiver filed");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "上传失败" : "Upload failed")); }
  };
  const uploadEvidence = async (file) => {
    const t = evTask.current;
    if (!t) return;
    setEvBusy(t.id);
    try {
      const up = await chunkUpload(api, file);
      await api.post(`/projects/${id}/engine/tasks/${t.id}/evidence`, up);
      toast.success(zh ? "证据已上传 ✓ 现在可以完成任务" : "Evidence uploaded");
      await load(true);
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "上传失败" : "Upload failed")); }
    setEvBusy(null);
  };
  const aiAdvice = async () => {
    setAdviceBusy(true);
    try { const { data: r } = await api.post(`/projects/${id}/engine/ai-advice`); setAdvice(r.advice); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "AI 暂不可用" : "AI unavailable")); }
    setAdviceBusy(false);
  };
  const addBlocker = async () => {
    if (!blkForm?.title?.trim()) { toast.error(zh ? "请输入阻塞项标题" : "Title required"); return; }
    await run(() => api.post(`/projects/${id}/engine/blockers`, blkForm), zh ? "已登记阻塞项（全局阻塞）" : "Blocker added");
    setBlkForm(null);
  };
  const closeBlocker = (b) => {
    const resolution = window.prompt(zh ? "解决说明：" : "Resolution:", T("已解决"));
    if (resolution === null) return;
    run(() => api.post(`/projects/${id}/engine/blockers/${b.id}/close`, { resolution }), zh ? "阻塞项已关闭" : "Closed");
  };

  if (err) return <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-slate-400 text-xs" data-testid="engine-error">{err}</div>;
  if (!eng) return <div className="bg-white rounded-xl border border-slate-200 p-8 flex justify-center" data-testid="engine-loading"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>;

  const ns = eng.nextStep || {};
  const [nsBg, nsLabelZh, nsLabelEn] = NEXT_META[ns.status] || NEXT_META.READY;
  const nsLabel = zh ? nsLabelZh : nsLabelEn;
  const subs = eng.substages.filter((s) => s.phase === tab);
  const openBlockers = (eng.blockers || []).filter((b) => b.status === "open");

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="engine-panel">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
          <Cpu className="h-4 w-4 text-indigo-600" />{zh ? "工程AI引擎" : "Engine"}
          {/* 【2026-10-29】任务数改为按服务端数据实时统计，不再写死 81 —— 那个数字来自知识库表，
              而 JSON 实际早已不是 81（踏勘五项合并成一张卡，又新增了框架两项），写死必然漂移。 */}
          <span className="text-[9px] font-normal text-slate-300">{eng.version} · {zh
            ? `${(eng.phases || []).length}阶段/${(eng.substages || []).length}子阶段/${(eng.substages || []).reduce((n, s) => n + (s.tasks || []).length, 0)}任务卡`
            : `${(eng.phases || []).length} phases / ${(eng.substages || []).length} sub-phases / ${(eng.substages || []).reduce((n, s) => n + (s.tasks || []).length, 0)} tasks`}</span>
        </p>
        <div className="flex items-center gap-2">
          <button data-testid="engine-blockers-btn" onClick={() => setShowBlockers(!showBlockers)}
            className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${openBlockers.length ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}>
            <ShieldAlert className="h-3 w-3 inline mr-0.5" />{zh ? "阻塞项" : "Blockers"} {openBlockers.length}
          </button>
          <Button data-testid="engine-ai-advice-btn" size="sm" variant="outline" onClick={aiAdvice} disabled={adviceBusy} className="h-7 text-[11px]">
            {adviceBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Bot className="h-3 w-3 mr-1" />}{zh ? "AI 解读" : "AI advice"}
          </Button>
        </div>
      </div>

      <div className={`rounded-lg ${nsBg} text-white px-3 py-2 mb-3`} data-testid="engine-next-step">
        <p className="text-[11px] font-black tracking-wide">{nsLabel}{ns.subName ? ` · ${zh ? ns.subName : (ns.subNameEn || ns.subName)}` : ""}</p>
        <p className="text-xs font-semibold mt-0.5">{(zh ? ns.title : (ns.titleEn || ns.title)) || (zh ? ns.label : (ns.labelEn || ns.label)) || "-"}
          {ns.role && <span className="ml-1.5 text-[10px] font-normal opacity-80">→ {(zh ? eng.roles : (eng.rolesEn || eng.roles))[ns.role] || ns.role}</span>}
          {ns.unlock && <span className="ml-1.5 text-[10px] font-normal opacity-80">{ns.unlock} {zh ? "解锁" : "unlocks"}</span>}
        </p>
        {(ns.reasons || []).length > 0 && <p className="text-[10px] opacity-90 mt-0.5">{(ns.reasons || []).join("；")}</p>}
      </div>

      {showBlockers && (
        <div className="rounded-lg border border-red-200 bg-red-50/50 p-2.5 mb-3 space-y-1.5" data-testid="engine-blockers-panel">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold text-red-700">{zh ? "全局阻塞项（未关闭时暂停当前子阶段推进）" : "Blockers"}</p>
            <button data-testid="engine-blocker-add" onClick={() => setBlkForm(blkForm ? null : { title: "", owner: "", dueDate: "" })} className="text-red-600 hover:text-red-800"><Plus className="h-3.5 w-3.5" /></button>
          </div>
          {blkForm && (
            <div className="flex gap-1.5 items-center flex-wrap" data-testid="engine-blocker-form">
              <input value={blkForm.title} onChange={(e) => setBlkForm({ ...blkForm, title: e.target.value })} placeholder={zh ? "阻塞项标题（如：市府整改单未闭环）" : "Title"} className="flex-1 min-w-[140px] text-[11px] rounded border border-red-200 px-1.5 py-1" data-testid="engine-blocker-title" />
              <input value={blkForm.owner} onChange={(e) => setBlkForm({ ...blkForm, owner: e.target.value })} placeholder={zh ? "责任人" : "Owner"} className="w-20 text-[11px] rounded border border-red-200 px-1.5 py-1" data-testid="engine-blocker-owner" />
              <input type="date" value={blkForm.dueDate} onChange={(e) => setBlkForm({ ...blkForm, dueDate: e.target.value })} className="text-[11px] rounded border border-red-200 px-1.5 py-1" data-testid="engine-blocker-due" />
              <Button size="sm" onClick={addBlocker} className="h-6 text-[11px] bg-red-600 hover:bg-red-700 text-white" data-testid="engine-blocker-submit">{zh ? "登记" : "Add"}</Button>
            </div>
          )}
          {openBlockers.length === 0 && !blkForm && <p className="text-[11px] text-slate-400">{zh ? "无未关闭阻塞项 ✓" : "None"}</p>}
          {openBlockers.map((b) => (
            <div key={b.id} className="flex items-center gap-2 text-[11px] bg-white rounded px-2 py-1" data-testid={`engine-blocker-${b.id}`}>
              <AlertTriangle className="h-3 w-3 text-red-500 shrink-0" />
              <span className="flex-1 min-w-0 truncate font-medium text-slate-700">{T(b.title)}<span className="text-slate-400 font-normal">{b.owner ? ` · ${b.owner}` : ""}{b.dueDate ? ` · ${zh ? "截止" : "due "}${String(b.dueDate).slice(5)}` : ""}</span></span>
              <button data-testid={`engine-blocker-close-${b.id}`} onClick={() => closeBlocker(b)} className="text-[10px] font-semibold text-emerald-600 hover:underline shrink-0">{zh ? "关闭" : "Close"}</button>
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <div className="flex items-center gap-3 flex-wrap rounded-lg bg-slate-50 px-2.5 py-1.5 mb-3 text-[11px]" data-testid="engine-gates-row">
          <label className="flex items-center gap-1 cursor-pointer" data-testid="engine-gate-permit">
            <input type="checkbox" checked={!!eng.permitPulled} onChange={(e) => setGate("permitPulled", e.target.checked)} className="accent-emerald-600" />
            <span className={eng.permitPulled ? "text-emerald-700 font-semibold" : "text-red-600 font-semibold"}>{zh ? "Permit 已 pulled" : "Permit pulled"}</span>
          </label>
          <label className="flex items-center gap-1 cursor-pointer" data-testid="engine-gate-set1">
            <input type="checkbox" checked={!!eng.set1Complete} onChange={(e) => setGate("set1Complete", e.target.checked)} className="accent-emerald-600" />
            <span className={eng.set1Complete ? "text-emerald-700 font-semibold" : "text-red-600 font-semibold"}>{zh ? "Set 1 图纸齐全" : "Set 1 drawings complete"}</span>
          </label>
          <span className="text-slate-300">|</span>
          <span className="text-slate-500">{zh ? "拆除判定：" : "Demo: "}
            {eng.demoNeeded === true && <span className="font-semibold text-orange-600">{zh ? "需要拆除" : "needed"}</span>}
            {eng.demoNeeded === false && <span className="font-semibold text-emerald-600">{zh ? "无需拆除（已留档）" : "not needed"}</span>}
            {eng.demoNeeded == null && (
              <>
                <button data-testid="engine-demo-yes" onClick={demoYes} className="ml-1 font-semibold text-indigo-600 hover:underline">{zh ? "需要" : "Yes"}</button>
                <span className="text-slate-300 mx-0.5">/</span>
                <button data-testid="engine-demo-no" onClick={() => demoInput.current?.click()} className="font-semibold text-indigo-600 hover:underline">{zh ? "无需(传豁免文件)" : "No (waiver)"}</button>
                <input ref={demoInput} type="file" className="hidden" data-testid="engine-demo-waiver-input"
                  onChange={(e) => e.target.files?.[0] && demoNoFile(e.target.files[0])} />
              </>
            )}
          </span>
        </div>
      )}

      <div className="flex gap-1 overflow-x-auto pb-1.5 mb-2">
        {eng.phases.map((p) => (
          <button key={p.idx} data-testid={`engine-phase-tab-${p.idx}`} onClick={() => { setTab(p.idx); setOpenSub(null); }}
            className={`shrink-0 text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${tab === p.idx ? "bg-slate-900 text-white border-slate-900" : p.idx === eng.phaseIdx ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
            {p.idx === eng.phaseIdx && <span className="mr-0.5">▶</span>}{zh ? p.zh : (p.en || p.zh)} {p.done}/{p.total}
            {p.blocked > 0 && <span className="ml-0.5 text-red-500">●</span>}
          </button>
        ))}
      </div>

      <div className="space-y-1.5 max-h-[560px] overflow-y-auto pr-1">
        {subs.map((s) => (
          <div key={s.key} className={`rounded-lg border ${s.current ? "border-amber-300 bg-amber-50/40" : s.satisfied ? "border-emerald-100 bg-emerald-50/30" : "border-slate-100"}`} data-testid={`engine-sub-${s.key}`}>
            <button className="w-full flex items-center gap-2 px-2.5 py-2 text-left" onClick={() => setOpenSub(openSub === s.key ? null : s.key)} data-testid={`engine-sub-toggle-${s.key}`}>
              {openSub === s.key ? <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
              <span className={`h-4 w-4 rounded-full shrink-0 flex items-center justify-center ${s.satisfied ? "bg-emerald-500" : s.current ? "bg-amber-400" : "bg-slate-200"}`}>
                {s.satisfied ? <CheckCircle2 className="h-3 w-3 text-white" /> : <span className="text-[8px] font-bold text-white">{s.done}</span>}
              </span>
              <span className="text-xs font-semibold text-slate-700 flex-1 min-w-0 truncate">{s.key} {zh ? s.name : (s.en || s.name)}
                {s.current && <span className="ml-1.5 text-[9px] font-bold text-amber-600">{zh ? "← 当前" : "current"}</span>}
                {s.any && <span className="ml-1 text-[9px] text-slate-400">{zh ? "(任一完成即可)" : "(any)"}</span>}
              </span>
              <span className="text-[10px] text-slate-400 shrink-0">{s.done}/{s.total}</span>
            </button>
            {openSub === s.key && (
              <div className="px-2.5 pb-2 space-y-1">
                {s.tasks.map((t) => {
                  const [slZh, slEn, sc] = STATE_META[t.state] || STATE_META.pending;
                  const sl = zh ? slZh : slEn;
                  const evCount = (t.evidence || []).length;
                  return (
                    <div key={t.id} className={`rounded-lg px-2 py-1.5 ${t.state === "blocked" ? "bg-red-50/70" : t.state === "ready" ? "bg-emerald-50/50" : "bg-white border border-slate-50"}`} data-testid={`engine-task-${t.id}`}>
                      <div className="flex items-start gap-2">
                        <span className={`shrink-0 mt-0.5 h-5 w-5 rounded-full text-[9px] font-bold flex items-center justify-center ${t.status === "done" ? "bg-emerald-500 text-white" : t.status === "skipped" ? "bg-slate-200 text-slate-400" : t.state === "blocked" ? "bg-red-500 text-white" : t.state === "ready" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                          {t.status === "done" ? <CheckCircle2 className="h-3 w-3" /> : t.status === "skipped" ? <CircleSlash className="h-3 w-3" /> : t.state === "locked" ? <Lock className="h-2.5 w-2.5" /> : t.seq}
                        </span>
                        <div className="min-w-0 flex-1" title={t.duty || ""}>
                          <p className={`text-xs font-medium ${t.status === "done" ? "text-slate-400 line-through" : t.status === "skipped" ? "text-slate-300" : "text-slate-700"}`}>{zh ? t.title : (t.titleEn || t.title)}</p>
                          <p className="flex items-center gap-1 mt-0.5 flex-wrap">
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${sc}`}>{sl}</span>
                            <span className={`text-[9px] px-1 rounded ${ROLE_COLOR[t.role] || "bg-slate-100 text-slate-500"}`}>{(zh ? eng.roles : (eng.rolesEn || eng.roles))[t.role] || t.role}</span>
                            {(t.assignee || (canManage && t.status !== "done" && t.status !== "skipped")) && (
                              <button data-testid={`engine-task-assign-btn-${t.id}`} disabled={!canManage}
                                onClick={() => canManage && setAssignFor(assignFor === t.id ? null : t.id)}
                                className={`text-[9px] px-1.5 py-0.5 rounded-full ${t.assignee ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-400"} ${canManage ? "hover:bg-indigo-100" : ""}`}>
                                <UserRound className="h-2.5 w-2.5 inline mr-0.5" />{t.assignee || (zh ? "指派" : "assign")} <CoTag kind={t.assigneeKind} zh={zh} />
                              </button>
                            )}
                            {t.requiresEvidence && <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${evCount ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}><Paperclip className="h-2.5 w-2.5 inline" /> {zh ? "证据" : "ev."} {evCount}</span>}
                            {t.unlock && <span className="text-[9px] text-amber-600"><Clock className="h-2.5 w-2.5 inline" /> {t.unlock} {zh ? "解锁" : "unlocks"}</span>}
                            {t.status === "done" && t.doneBy && <span className="text-[9px] text-slate-400">{t.doneBy} · {fmtMD(t.doneAt)}</span>}
                            {t.status === "skipped" && t.skipReason && <span className="text-[9px] text-slate-400">{t.skipReason}</span>}
                            {t.status === "waiting_external" && t.waitingNote && <span className="text-[9px] text-blue-600">{t.waitingNote}</span>}
                          </p>
                          {(t.reasons || []).length > 0 && t.state === "blocked" && <p className="text-[10px] text-red-600 mt-0.5">{t.reasons.join("；")}</p>}
                          {assignFor === t.id && (
                            <select autoFocus data-testid={`engine-task-assign-select-${t.id}`} value={t.assigneeUsername || ""}
                              onChange={(e) => assignTask(t, e.target.value)}
                              className="mt-1 w-full text-[10px] rounded border border-indigo-200 px-1 py-0.5 bg-white">
                              <option value="">{zh ? "— 取消指派 —" : "Unassigned"}</option>
                              <AssigneeOptions projectStaff={eng.projectStaff} staff={eng.staff} zh={zh} T={T} />
                            </select>
                          )}
                        </div>
                        <span className="flex items-center gap-1 shrink-0 flex-wrap justify-end max-w-[130px]">
                          {(t.state === "ready" || t.state === "pending") && (
                            <>
                              {t.requiresEvidence && (
                                <button data-testid={`engine-task-evidence-${t.id}`} disabled={evBusy === t.id}
                                  onClick={() => { evTask.current = t; evInput.current?.click(); }}
                                  className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-indigo-100 text-indigo-700 hover:bg-indigo-200">
                                  {evBusy === t.id ? "…" : (zh ? "传证据" : "Evidence")}
                                </button>
                              )}
                              <button data-testid={`engine-task-complete-${t.id}`} onClick={() => complete(t)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-emerald-600 text-white hover:bg-emerald-700">{zh ? "完成" : "Done"}</button>
                              <button data-testid={`engine-task-waiting-${t.id}`} onClick={() => waiting(t)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-blue-100 text-blue-700 hover:bg-blue-200">{zh ? "等外部" : "Wait"}</button>
                              {t.optional && <button data-testid={`engine-task-skip-${t.id}`} onClick={() => skip(t)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-slate-100 text-slate-500 hover:bg-slate-200">{zh ? "跳过" : "Skip"}</button>}
                            </>
                          )}
                          {t.state === "waiting_time" && canRelease && <button data-testid={`engine-task-release-${t.id}`} onClick={() => release(t)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-amber-500 text-white hover:bg-amber-600"><Unlock className="h-2.5 w-2.5 inline" /> Release</button>}
                          {t.state === "waiting_external" && (
                            <>
                              <button data-testid={`engine-task-resume-${t.id}`} onClick={() => clearWaiting(t)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-blue-600 text-white hover:bg-blue-700">{zh ? "已回复" : "Resume"}</button>
                              <button data-testid={`engine-task-complete-${t.id}`} onClick={() => complete(t)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-emerald-600 text-white hover:bg-emerald-700">{zh ? "完成" : "Done"}</button>
                            </>
                          )}
                          {(t.state === "blocked" || t.state === "waiting_external") && <button data-testid={`engine-task-nudge-${t.id}`} onClick={() => nudge(t)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-amber-100 text-amber-700 hover:bg-amber-200">{zh ? "催办" : "Nudge"}</button>}
                          {(t.status === "done" || t.status === "skipped") && canManage && <button data-testid={`engine-task-reopen-${t.id}`} onClick={() => reopen(t)} className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-slate-100 text-slate-500 hover:bg-slate-200"><RotateCcw className="h-2.5 w-2.5 inline" /> {zh ? "重开" : "Reopen"}</button>}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        {subs.length === 0 && <p className="text-xs text-slate-300 text-center py-6">{zh ? "该阶段无任务卡" : "No tasks"}</p>}
      </div>
      <input ref={evInput} type="file" className="hidden" data-testid="engine-evidence-file-input"
        onChange={(e) => { if (e.target.files?.[0]) uploadEvidence(e.target.files[0]); e.target.value = ""; }} />

      {advice && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setAdvice(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="engine-ai-advice-modal">
            <p className="font-bold text-slate-800 mb-1 flex items-center gap-1.5"><Bot className="h-4 w-4 text-indigo-600" />{zh ? "AI 解读与建议" : "AI advice"}</p>
            <p className="text-[10px] text-slate-400 mb-3"><ShieldCheck className="h-3 w-3 inline mr-0.5" />{zh ? "推进判定由确定性规则引擎做出，AI 仅提供解读与建议" : "Decisions are rule-based; AI only explains."}</p>
            <pre className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed max-h-[55vh] overflow-y-auto">{advice}</pre>
            <div className="flex justify-end mt-4">
              <Button size="sm" className="h-7 text-xs" onClick={() => setAdvice(null)} data-testid="engine-ai-advice-close">{zh ? "关闭" : "Close"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
